import { lazy } from 'react';

// NAV-FE-1b — `React.lazy` that survives a deploy in the middle of a session.
//
// Every page is now its own chunk, named by a content hash. A tab opened before
// a deploy still holds the OLD `main.*.js`, which asks for OLD chunk names the
// new deploy no longer serves: the dynamic import rejects. The honest fix is to
// load the new build, so the first such failure reloads the page ONCE. A mark in
// sessionStorage (`cqa.chunkReload:<name>`) stops a second reload: if the chunk
// still fails after a fresh page, the error is real (offline, a broken deploy)
// and it is allowed to surface. A successful load clears the mark, so the next
// deploy gets its one reload too.

const markKey = (name) => `cqa.chunkReload:${name}`;

const readMark = (name) => {
  try { return window.sessionStorage.getItem(markKey(name)); } catch { return null; }
};
const writeMark = (name) => {
  try { window.sessionStorage.setItem(markKey(name), '1'); } catch { /* storage blocked */ }
};
const clearMark = (name) => {
  try { window.sessionStorage.removeItem(markKey(name)); } catch { /* storage blocked */ }
};

/** The failures a stale tab produces when its chunk is gone (webpack, Chrome, Safari, Firefox). */
export const isChunkLoadError = (err) => {
  if (!err) return false;
  if (err.name === 'ChunkLoadError') return true;
  const message = String(err.message || err);
  return /Loading (CSS )?chunk [\w-]+ failed|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i
    .test(message);
};

/** The import with its one reload. Exported for the unit tests; pages use `lazyWithReload`. */
export const loadWithReload = (factory, name, reload = () => window.location.reload()) => factory().then(
  (mod) => {
    clearMark(name);
    return mod;
  },
  (err) => {
    if (isChunkLoadError(err) && !readMark(name)) {
      writeMark(name);
      reload();
      // The page is going away; never settle, so nothing renders an error first.
      return new Promise(() => {});
    }
    throw err;
  },
);

/** `const X = lazyWithReload(() => import('./pages/X'), 'X')` — `name` keys the reload mark. */
export default function lazyWithReload(factory, name) {
  return lazy(() => loadWithReload(factory, name));
}
